import { useMemo, useState } from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarInset,
  SidebarTrigger
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Shield, CreditCard, Settings, LifeBuoy, BarChart2, MessageSquare, UserCog, LogOut, Wallet,
  LayoutDashboard, ScrollText, Search, Building, CalendarCheck, Gavel, Users, Banknote
} from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ThemeToggle from "@/components/ThemeToggle";

// Import components for tabs
import AdminOverview from "@/components/admin/AdminOverview";
import ComplianceCenter from "@/components/admin/ComplianceCenter";
import ContentModeration from "@/components/admin/ContentModeration";
import PaymentSettings from "@/components/admin/PaymentSettings";
import PlatformSettings from "@/components/admin/PlatformSettings";
import PaymentsAndSubscriptions from "@/components/admin/PaymentsAndSubscriptions";
import SettingsAndConfigurations from "@/components/admin/SettingsAndConfigurations";
import SupportCenter from "@/components/admin/SupportCenter";
import GlobalAnalytics from "@/components/admin/GlobalAnalytics";
import ConsultationManagement from "@/components/admin/ConsultationManagement";
import RoleManagement from "@/components/admin/RoleManagement";
import PayoutsAdmin from "@/components/admin/PayoutsAdmin";
import LivePayments from "@/components/admin/LivePayments";
import VerificationReview from "@/components/admin/VerificationReview";
import AgencyApprovals from "@/components/admin/AgencyApprovals";
import AccountControls from "@/components/admin/AccountControls";
import EventApprovals from "@/components/admin/EventApprovals";
import PolicyEnforcement from "@/components/admin/PolicyEnforcement";
import PortalIdBadge from "@/components/admin/PortalIdBadge";

type MenuItem = {
  id: string;
  label: string;
  icon: typeof Shield;
  group: string;
  render: (nav: (id: string) => void) => JSX.Element;
};

const menuItems: MenuItem[] = [
  // Command
  { id: "overview", label: "Admin Dashboard", icon: LayoutDashboard, group: "Command", render: () => <AdminOverview /> },
  { id: "analytics", label: "Global Analytics", icon: BarChart2, group: "Command", render: () => <GlobalAnalytics /> },

  // Governance & compliance
  { id: "compliance", label: "Compliance Center", icon: ScrollText, group: "Governance", render: (nav) => <ComplianceCenter onNavigate={nav} /> },
  { id: "venue-badges", label: "Venue Badge Verification", icon: Shield, group: "Governance", render: () => <VerificationReview filterType="venue" /> },
  { id: "event-approvals", label: "Event Approvals", icon: CalendarCheck, group: "Governance", render: () => <EventApprovals /> },
  { id: "content-moderation", label: "Content Moderation", icon: Shield, group: "Governance", render: () => <ContentModeration /> },
  { id: "policy-enforcement", label: "Policy Enforcement", icon: Gavel, group: "Governance", render: () => <PolicyEnforcement /> },

  // People & portals
  { id: "account-controls", label: "Account Controls", icon: Users, group: "People", render: () => <AccountControls /> },
  { id: "role-management", label: "Role Management", icon: UserCog, group: "People", render: () => <RoleManagement /> },
  { id: "agency-onboarding", label: "Agency Onboarding", icon: Building, group: "People", render: () => <AgencyApprovals /> },
  { id: "consultations", label: "Consultations", icon: MessageSquare, group: "People", render: () => <ConsultationManagement /> },

  // Revenue
  { id: "payments", label: "Payments & Subscriptions", icon: CreditCard, group: "Revenue", render: () => <PaymentsAndSubscriptions /> },
  { id: "payouts", label: "Payouts", icon: Wallet, group: "Revenue", render: () => <PayoutsAdmin /> },
  { id: "live-payments", label: "Live Payments", icon: Banknote, group: "Revenue", render: () => <LivePayments /> },

  // Operations
  { id: "support", label: "Support Center", icon: LifeBuoy, group: "Operations", render: () => <SupportCenter /> },
  { id: "payment-settings", label: "Payment Settings", icon: CreditCard, group: "Operations", render: () => <PaymentSettings /> },
  { id: "platform-settings", label: "Platform Settings", icon: Settings, group: "Operations", render: () => <PlatformSettings /> },
  { id: "settings", label: "Settings & Config", icon: Settings, group: "Operations", render: () => <SettingsAndConfigurations /> },
];

const GROUP_ORDER = ["Command", "Governance", "People", "Revenue", "Operations"];


const PlatformControlDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [navSearch, setNavSearch] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
    navigate("/landing-page-2");
  };

  const grouped = useMemo(() => {
    const term = navSearch.trim().toLowerCase();
    const visible = term ? menuItems.filter((i) => i.label.toLowerCase().includes(term)) : menuItems;
    return GROUP_ORDER.map((group) => ({ group, items: visible.filter((i) => i.group === group) })).filter(
      (g) => g.items.length > 0,
    );
  }, [navSearch]);

  const activeMenuItem = menuItems.find(item => item.id === activeTab);

  return (
    <div className="min-h-screen bg-background">
      <SidebarProvider>
        <div className="flex w-full min-h-screen">
          <Sidebar className="border-r border-border bg-card shadow-lg">
            <SidebarHeader className="p-6 border-b border-border bg-card">
              <div className="flex items-center space-x-3">
                <img 
                  src="/lovable-uploads/metro-city-groove-logo-white.png" 
                  alt="MCG Agency Logo" 
                  className="w-12 h-12 object-contain bg-white rounded-lg p-1 shadow-sm invert text-black"
                />
                <div>
                  <h1 className="text-lg font-bold text-foreground">Platform Control</h1>
                  <p className="text-xs text-muted-foreground">Governance & Compliance</p>
                </div>
              </div>
              <div className="relative mt-4">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={navSearch}
                  onChange={(e) => setNavSearch(e.target.value)}
                  placeholder="Jump to a module…"
                  className="pl-9 h-9"
                />
              </div>
            </SidebarHeader>
            
            <SidebarContent className="p-3">
              {grouped.map(({ group, items }) => (
                <SidebarGroup key={group}>
                  <SidebarGroupLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {group}
                  </SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {items.map((item) => (
                        <SidebarMenuItem key={item.id}>
                          <SidebarMenuButton
                            onClick={() => setActiveTab(item.id)}
                            isActive={activeTab === item.id}
                            className={`w-full justify-start text-left px-3 py-2.5 rounded-lg transition-all duration-200 ${
                              activeTab === item.id
                                ? 'bg-primary text-primary-foreground shadow-md font-semibold'
                                : 'text-foreground hover:bg-accent font-medium'
                            }`}
                          >
                            <item.icon className="w-4 h-4 mr-3 shrink-0" />
                            <span className="text-sm">{item.label}</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              ))}
            </SidebarContent>
          </Sidebar>

          <SidebarInset className="flex-1 bg-background">
            <header className="sticky top-0 flex h-16 shrink-0 items-center gap-2 border-b border-border bg-card/95 backdrop-blur-md px-6 shadow-sm z-10">
              <SidebarTrigger className="text-foreground hover:bg-accent rounded-md p-2" />
              <div className="flex items-center gap-3">
                {activeMenuItem && (
                  <>
                    <activeMenuItem.icon className="w-5 h-5 text-foreground" />
                    <div>
                      <h2 className="text-lg font-bold text-foreground leading-tight">{activeMenuItem.label}</h2>
                      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{activeMenuItem.group}</p>
                    </div>
                  </>
                )}
              </div>
              <div className="ml-auto flex items-center gap-3">
                <PortalIdBadge portalCode="PC" />
                <ThemeToggle />
                <Button
                  onClick={handleLogout}
                  variant="default"
                  className="flex items-center gap-2 shadow-md"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </div>
            </header>

            <main className="flex-1 p-6">
              {activeMenuItem?.render(setActiveTab)}
            </main>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  );
};

export default PlatformControlDashboard;

